import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import Navbar from "../components/Navbar";
import Footer from "../components/Footer";
import { Buttons } from "../components/Buttons";
import QuantitySelector from "../components/QuantitySelector";
import { Comments, Details, ShippingInfo } from "../components/Products";
import { addToCart } from "../redux/store/cartSlice";
// import NewsTicker from "../components/Home/Newtricker";
import { toast } from "react-toastify";
import ProductCard from "../components/Home/ProductCard";
import { FaAngleLeft, FaAngleRight, FaRegHeart } from "react-icons/fa";
import { addToWishlistAPI } from "../redux/store/wishlist";
import WishlistButton from "../components/WishlistButton";
import { server } from "../utilis/constant";
import { fetchProducts } from "../redux/api/products";
import { useAuth } from "../contexts/AuthContext";

const ProductDetails = () => {
  const { id } = useParams();
  const dispatch = useDispatch();
  const sliderRef = useRef(null);

  const { allProducts } = useSelector((state) => state.products);
  const { isLoggedIn } = useSelector((state) => state.user);
  const wishlistItems = useSelector((state) => state.wishlist.wishlistItems);
  const { openLoginModal } = useAuth();

  const [activeTab, setActiveTab] = useState(1);
  const [quantity, setQuantity] = useState(1);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [activeVariantId, setActiveVariantId] = useState(null);
  const [activeImage, setActiveImage] = useState(null);
  const [cartDrawer, setCartDrawer] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, [id]);
  // ✅ Fixed: MongoDB uses _id
  const product = useMemo(() => {
    return allProducts.find((p) => String(p._id) === String(id)) || null;
  }, [allProducts, id]);

  const galleryImages = useMemo(() => {
    if (!product) return [];

    const variantImages =
      product.variants
        ?.map((variant) => variant.image)
        .filter(Boolean) || [];
    const sourceImages = variantImages.length > 0 ? variantImages : [product.image];

    return [...new Set(sourceImages.filter(Boolean))];
  }, [product]);

  useEffect(() => {
    if (!allProducts?.length) {
      dispatch(fetchProducts());
    }
  }, []);

  // ✅ Fixed: variant uses .image directly, no imageId
  useEffect(() => {
    if (!product) return;

    if (product.variants?.length > 0) {
      const first = product.variants[0];
      setSelectedVariant(first);
      setActiveVariantId(first._id);
      setActiveImage(first.image || galleryImages[0] || product.image || null);
    } else {
      setSelectedVariant(null);
      setActiveVariantId(null);
      setActiveImage(galleryImages[0] || product.image || null);
    }
  }, [product, galleryImages]);

  // ✅ Fixed: useMemo + _id + tags trim
  const relatedProducts = useMemo(() => {
    if (!product) return [];
    return allProducts.filter((p) => {
      if (String(p._id) === String(product._id)) return false;
      const sameVendor = p.vendor === product.vendor;
      const commonTags = p.tags?.some((tag) =>
        product.tags?.includes(tag.trim()),
      );
      return sameVendor || commonTags;
    });
  }, [allProducts, product]);

  const scrollLeft = () => {
    sliderRef.current?.scrollBy({ left: -300, behavior: "smooth" });
  };

  const scrollRight = () => {
    if (!sliderRef.current) return;
    const { scrollLeft, scrollWidth, clientWidth } = sliderRef.current;
    if (scrollLeft >= scrollWidth - clientWidth - 10) {
      sliderRef.current.scrollTo({ left: 0, behavior: "smooth" });
    } else {
      sliderRef.current.scrollBy({ left: 300, behavior: "smooth" });
    }
  };

  const handleAddToCart = () => {
    dispatch(addToCart({ ...product, variant: selectedVariant, quantity }));
    toast.success("Added to cart");
    setCartDrawer(true);
  };

  const handleWishlist = async () => {
    if (!isLoggedIn) {
      openLoginModal();
      return;
    }

    try {
      await dispatch(addToWishlistAPI(product._id)).unwrap();
      toast.success("Added to wishlist");
    } catch (error) {
      console.error('Wishlist error:', error);
      if (error.includes('Access denied') || error.includes('authentication token')) {
        // Token might be invalid, show login modal
        openLoginModal();
      } else if (error.includes('already in wishlist')) {
        toast.error("Already in wishlist");
      } else {
        toast.error(error || "Failed to add to wishlist");
      }
    }
  };

  // ✅ Loading states
  if (allProducts.length === 0) {
    return <div className="text-center py-20">Loading...</div>;
  }
  if (!product) {
    return <div className="text-center py-20">Product Not Found</div>;
  }

  return (
    <>
      <div className="bg-black">
        <Navbar />
      </div>
      <div className="bg-white py-[1rem] px-[1rem] lg:px-[6rem]">
        {/* Breadcrumb */}
        <div className="bg-white w-full py-[1rem] mb-[2rem] px-[1rem] sm:px-[5rem] text-[16px] text-gray-500">
          <Link to="/" className="text-gray-800">
            Home
          </Link>{" "}
          {">"} {product.title}
        </div>

        <div className="flex flex-col lg:flex-row gap-10 mb-12">
          {/* ================= LEFT - IMAGE GALLERY ================= */}
          <div className="flex-1 lg:sticky lg:top-10 self-start">
            {/* Main Image */}
            <div className="bg-white border rounded-2xl shadow-sm w-full h-[500px] flex justify-center items-center p-6">
              {activeImage && (
                <img
                  src={`${server}/${activeImage}`}
                  alt={product.title}
                  className="w-full h-full object-contain transition-all duration-300"
                />
              )}
            </div>

            {/* Thumbnails */}
            <div className="flex gap-3 mt-4 overflow-x-auto pb-2">
              {galleryImages.map((image) => (
                <img
                  key={image}
                  src={`${server}/${image}`}
                  onClick={() => setActiveImage(image)}
                  alt={product.title}
                  className={`w-20 h-20 rounded-xl cursor-pointer border-2 transition-all hover:scale-105 ${
                    activeImage === image
                      ? "border-black shadow-md"
                      : "border-gray-200"
                  }`}
                />
              ))}
            </div>
          </div>

          {/* ================= RIGHT - PRODUCT DETAILS ================= */}
          <div className="flex-1 flex flex-col gap-6">
            {/* Title */}
            <div>
              <h1 className="text-3xl font-bold text-gray-900 leading-snug">
                {product.title}
              </h1>
              <p className="text-gray-500 mt-1">
                {product.category} • {product.type}
              </p>
            </div>

            <hr />

            {/* STOCK STATUS */}
            {selectedVariant && (
              <div className="flex items-center gap-2 text-sm">
                <span
                  className={`w-3 h-3 rounded-full ${
                    selectedVariant.qty > 0 ? "bg-green-500" : "bg-red-500"
                  }`}
                />
                <span className="font-semibold">
                  {selectedVariant.qty > 0 ? "In Stock" : "Sold Out"}
                </span>
                <span className="text-gray-500">
                  ({selectedVariant.qty} left)
                </span>
              </div>
            )}

            {/* PRODUCT META */}
            <div className="grid grid-cols-2 gap-y-2 text-sm text-gray-600">
              {product.vendor && (
                <span>
                  <b>Vendor:</b> {product.vendor}
                </span>
              )}
              {product.sku && (
                <span>
                  <b>SKU:</b> {product.sku}
                </span>
              )}
              {product.tags?.length > 0 && (
                <span className="col-span-2">
                  <b>Tags:</b> {product.tags.join(", ")}
                </span>
              )}
            </div>

            {/* VARIANTS */}
            {product.variants?.length > 0 && (
              <div className="flex flex-wrap gap-3">
                {product.variants.map((variant) => (
                  <div
                    key={variant._id}
                    onClick={() => {
                      setActiveVariantId(variant._id);
                      setSelectedVariant(variant);
                      setActiveImage(variant.image || galleryImages[0] || product.image);
                    }}
                    className={`flex items-center gap-3 px-4 py-3 rounded-xl border cursor-pointer transition-all hover:shadow-md ${
                      activeVariantId === variant._id
                        ? "border-black bg-gray-50"
                        : "border-gray-200"
                    }`}
                  >
                    {variant.image && (
                      <img
                        src={`${server}/${variant.image}`}
                        className="w-12 h-12 object-contain"
                      />
                    )}

                    <div>
                      <p className="font-semibold text-sm">{variant.title}</p>
                      <p className="text-gray-600 text-sm">${variant.price}</p>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* PRICE + QUANTITY */}
            <div className="flex items-center justify-between flex-wrap gap-4">
              <div className="text-2xl font-bold text-black">
                ${selectedVariant ? selectedVariant.price : "—"}
              </div>

              <QuantitySelector
                value={quantity}
                onChange={setQuantity}
                max={selectedVariant?.qty || 1}
                min={1}
              />
            </div>

            {/* ACTIONS */}
            <div className="flex gap-4 items-center mt-2">
              <div className="flex-1">
                <Buttons title="Add to Cart" handleClick={handleAddToCart} />
              </div>

              <WishlistButton product={product} size="lg" />
            </div>
          </div>
        </div>
        {/* Tabs */}
        <div>
          <div className="flex gap-[1rem] items-center">
            {[
              { id: 1, label: "Details" },
              { id: 2, label: "Shipping Info" },
              { id: 3, label: "Reviews" },
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`py-2 px-2 text-[1rem] font-semibold cursor-pointer ${
                  activeTab === tab.id
                    ? "border-b-2 border-[#FFDD6A] text-gray-800"
                    : "text-gray-700"
                }`}
              >
                {tab.label}
              </button>
            ))}
          </div>
          <hr className="mb-[2rem]" />
          <div className="text-gray-700">
            {activeTab === 1 && <Details data={product} />}
            {activeTab === 2 && <ShippingInfo />}
            {activeTab === 3 && (
              <Comments title={product.title} products={product} />
            )}
          </div>
        </div>
      </div>

      {/* <NewsTicker /> */}

      {/* Related Products */}
      {relatedProducts.length > 0 && (
        <div>
          <div className="w-full text-center flex flex-col items-center justify-center gap-2 mt-[2rem]">
            <h1 className="font-syne text-2xl md:text-4xl font-bold">
              You May Also Like
            </h1>
          </div>

          <div className="bg-white pt-8 sm:pt-10 pb-10 overflow-x-hidden relative">
            <div className="container mx-auto px-3 sm:px-4">
              <div className="w-full relative">
                <div
                  ref={sliderRef}
                  className="related-products-track"
                >
                  {relatedProducts.map((item) => (
                    <div
                      key={item._id}
                      className="related-product-item min-w-0"
                    >
                      <ProductCard products={item} />
                    </div>
                  ))}
                </div>

                <button
                  onClick={scrollLeft}
                  className="hidden lg:flex absolute top-1/2 left-[-22px] w-11 h-11 bg-white rounded-full items-center justify-center -translate-y-1/2 z-20 shadow-lg hover:scale-110 transition-all"
                >
                  <FaAngleLeft className="text-black text-2xl" />
                </button>

                <button
                  onClick={scrollRight}
                  className="hidden lg:flex absolute top-1/2 right-[-22px] w-11 h-11 bg-white rounded-full items-center justify-center -translate-y-1/2 z-20 shadow-lg hover:scale-110 transition-all"
                >
                  <FaAngleRight className="text-black text-2xl" />
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </>
  );
};

export default ProductDetails;
